<template>
  <van-popup
    v-model:show="userInfoPopupConfig.isShow"
    position="center"
    class="user-info-popup"
    teleport="body"
    :close-on-popstate="true"
  >
    <div class="poster">
      <!-- 主照片 -->
      <img
        class="poster-img"
        :src="common.handlePicUrl(photoActive as string)"
        alt=""
      />
      <div class="relation flex v-c s-b">
        <!-- 顶部操作区域 关注-举办 -->
        <!-- 举报 -->
        <van-button
          v-if="userInfo.uid !== userInfoStorage?.uid"
          class="report"
          @click="reportRef?.open(targetUid)"
        ></van-button>
        <!-- 关注 -->
        <van-button
          v-if="userInfo.uid !== userInfoStorage?.uid"
          class="follow-box"
          @click="handleLike"
        >
          <div v-show="userInfoStorage?.isFans" class="followed"></div>
          <div v-show="!userInfoStorage?.isFans" class="follow"></div>
        </van-button>
      </div>
      <!-- 底部信息 -->
      <div class="info">
        <div class="info-item flex v-e s-b">
          <!--语言 -->
          <div class="flex column v-s">
            <div
              v-if="
                userInfoStorage.language?.secondLanguage &&
                (userInfoStorage.language?.firstLanguage ||
                  userInfoStorage.firstLanguage) !==
                  userInfoStorage.language?.secondLanguage
              "
              class="language"
            >
              {{ userInfoStorage.language?.secondLanguage }}
            </div>
            <div
              v-if="
                userInfoStorage.language?.firstLanguage ||
                userInfoStorage.firstLanguage
              "
              class="language"
            >
              {{
                userInfoStorage.language?.firstLanguage ||
                userInfoStorage.firstLanguage
              }}
            </div>
          </div>
          <!-- 其他照片 -->
          <ul v-if="userInfoStorage?.privatePhoto" class="private flex v-c">
            <li
              v-for="item in photoList"
              :key="item.pid"
              :class="{ active: photoActive === item.photoUrl }"
              @click="photoActive = item.photoUrl"
            >
              <van-image
                class="private-img"
                fit="cover"
                position="top"
                :src="common.handlePicUrl(item.photoUrl)"
              />
            </li>
          </ul>
        </div>

        <div class="info-item flex v-c s-b">
          <div class="name flex v-c">
            <!-- 名字 -->
            <div class="otw">{{ userInfoStorage?.nick }}</div>
            <!-- 性别-年龄 -->
            <div
              class="gender-age flex center"
              :class="'gender' + userInfoStorage?.gender"
            >
              <div class="gender-icon flex center">
                <img
                  v-show="userInfoStorage?.gender === 1"
                  class="gender-male-img"
                  src="@/assets/image/public/gender_male.png"
                  alt=""
                />
                <img
                  v-show="userInfoStorage?.gender === 2"
                  class="gender-woman-img"
                  src="@/assets/image/public/gender_woman.png"
                  alt=""
                />
              </div>
              <div class="age">{{ userInfoStorage.age }}</div>
            </div>
            <img
              v-if="
                userInfoStorage?.gender === 1 &&
                userInfoStorage?.videoRoomExperLevelPic
              "
              class="level-pic"
              :src="
                common.handlePicUrl(userInfoStorage?.videoRoomExperLevelPic)
              "
            />
            <img
              v-if="
                userInfoStorage?.gender === 2 && userInfoStorage?.charmLevelPic
              "
              class="level-pic"
              :src="common.handlePicUrl(userInfoStorage?.charmLevelPic)"
            />
          </div>
          <!-- 地址 -->
          <div class="address flex center">
            <template v-if="userInfoStorage?.country">
              <img
                class="address-icon"
                src="@/assets/image/userInfoPopup/address.png"
                alt=""
              />
              <div>{{ userInfoStorage?.country }}</div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <!-- 底部操作区域 拨打-聊天 -->
    <div
      v-if="userInfo.uid != targetUid"
      :class="`operate flex v-c ${userInfoPopupConfig.isCall ? 's-b' : 'h-c'}`"
    >
      <van-button
        v-if="userInfoPopupConfig.isCall"
        class="chat"
        @click="handleChat"
      ></van-button>
      <van-button class="call" @click="handleCall">
        <div class="call-content flex center">
          <div class="call-icon"></div>
          <div class="flex-1 flex column center">
            <div class="tips">{{ $t('UserInfoPopup.VC') }}</div>
            <div class="value flex center">
              <template
                v-if="
                  !userInfoStorage?.freeCall && userInfoStorage?.callDiamond
                "
              >
                <div class="diamond"></div>
                {{ userInfoStorage?.callDiamond }}/min
              </template>
              <template v-if="userInfoStorage?.freeCall">{{
                $t('UserInfoPopup.free')
              }}</template>
            </div>
          </div>
        </div>
      </van-button>
    </div>
  </van-popup>
  <Report ref="reportRef" />
</template>

<!-- 用户信息弹窗 -->
<script setup lang="ts">
defineOptions({ name: 'UserInfoPopup' })
import { apiRelationLike } from '@/api/user'
import type { userInfoInterface } from '@/types/apiType/userType'
import type { userInfoStorageMapInterface } from '@/types/viewsType/userType'
const i18n = useNuxtApp().$i18n

import type { Report } from '#build/components'
const reportRef = ref<InstanceType<typeof Report> | null>(null)

// userStore
const userStore = useUserStore()
const { userInfo, userInfoPopupConfig } = storeToRefs(userStore)

// 切换页面关闭弹窗
const router = useRouter()
router.afterEach(() => {
  userStore.closeUserInfoPopup()
})

// 会话
function handleChat() {
  const route = useRoute()
  router.push({
    path: '/chatPage',
    query: { sessionId: common.encryptParam('p2p-' + targetUid.value) },
    replace: route.path.indexOf('/room/call') !== -1 // 如果当前页面是在视频通话界面的就先关闭当前页面再跳转
  })
}

// 打电话
const callStore = useCallStore()
function handleCall() {
  callStore.dial(targetUid.value, 0, 6)
}

// 关注
async function handleLike() {
  const type = userInfoStorage.value?.isFans ? 2 : 1
  // 有用户信息才设置，要不然会报错
  if (userStore.userInfoStorageMap[targetUid.value]) {
    userStore.userInfoStorageMap[targetUid.value].isFans =
      type === 1 ? true : false
  }
  const res = await apiRelationLike({
    likedUid: targetUid.value,
    type //1关注,2取消关注
  })
  const { code } = res
  if (code === 200) {
    showToast(
      type === 1 ? i18n.t('UserInfoPopup.FS') : i18n.t('UserInfoPopup.US')
    )
    emitter.emit('userInfoPopupLike', type)
  }
}

// 用户信息
const info = ref<userInfoInterface>(defaultData.defaultUserInfo())
const targetUid = ref()
const userInfoStorage = computed(
  () =>
    (userStore.userInfoStorageMap[
      userInfoPopupConfig.value.uid
    ] as userInfoStorageMapInterface) || {}
)

// 监听是否显示
watch(
  userInfoPopupConfig,
  () => {
    if (userInfoPopupConfig.value.isShow) {
      info.value = { ...defaultData.defaultUserInfo() }
      targetUid.value = userInfoPopupConfig.value.uid
      console.log(userInfoStorage.value)
      photoActive.value = userInfoStorage.value.avatar

      userStore.publicGetUserInfo(targetUid.value).then((res) => {
        info.value = res || {}
        eventTracking.eventLog(eventTracking.eventId.open_user_info_page)
      })
    }
  },
  { deep: true }
)

// 照片功能 [头像,照片,照片] 只取前2张照片，
// 目前选中的，每次打开重置一次
const photoActive = ref<string>()
// 照片功能 [头像,照片,照片] 取前三张种种和头像不一样的照片
const photoList = computed(() => {
  const temporaryList = [{ pid: 1, photoUrl: userInfoStorage.value.avatar }]
  if (userInfoStorage.value.privatePhoto) {
    // 循环是为了设置 pid 为 1 2 3，因为有可能有pid为null的情况
    userInfoStorage.value.privatePhoto.slice(0, 3).forEach((v, i) => {
      // 因为后端返回的图片链接后缀不一样，可能一样的照片不一样的后缀，所以取？号前面链接做比较
      const photoUrl = v.photoUrl && v.photoUrl.split('?')[0]
      const avatar =
        userInfoStorage.value.avatar &&
        userInfoStorage.value.avatar.split('?')[0]
      if (photoUrl !== avatar && temporaryList.length < 3) {
        temporaryList.push({ pid: i + 2, photoUrl: v.photoUrl })
      }
    })
  }
  return temporaryList
})
</script>

<style lang="scss" scoped>
@import './scss/index.scss';
</style>
